import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Store, Building2, ShoppingCart, Users, PackageCheck } from 'lucide-react';
import { GeometricPattern } from './GeometricPattern';
export function HeroSection() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };
  return <div className="relative w-full min-h-screen flex flex-col lg:flex-row bg-[#0a1628] overflow-hidden" data-id="element-340">
      {/* Left Content Section */}
      <div className="relative z-20 w-full lg:w-[60%] flex flex-col justify-center px-6 lg:px-16 pt-40 pb-16 lg:py-0 lg:pt-24" data-id="element-341">
        {/* Diagonal Background for Desktop */}
        <div className="absolute inset-0 bg-[#0a1628] hidden lg:block" style={{
        clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)',
        zIndex: -1
      }} data-id="element-342" />

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="max-w-2xl" data-id="element-343">
          <div className="flex items-center gap-3 mb-6" data-id="element-344">
            <div className="h-px w-12 bg-[#d4a853]" data-id="element-345" />
            <span className="text-[#d4a853] font-medium tracking-[0.2em] uppercase text-sm" data-id="element-346">
              Global Product Sourcing & Market Expansion
            </span>
          </div>

          <h1 className="text-5xl lg:text-7xl font-bold text-white leading-[1.1] mb-8 tracking-tight" data-id="element-347">
            Your Strategic Bridge to <br data-id="element-348" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400" data-id="element-349">
              Global Retail Markets
            </span>
          </h1>

          <p className="text-lg text-gray-400 mb-10 max-w-lg leading-relaxed" data-id="element-350">
            We connect manufacturers and producers with leading hypermarkets,
            supermarket chains, and retail distributors across the UK, Europe,
            and Asia. We source, negotiate, supply, and expand.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-351">
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => scrollToSection('contact')} className="group relative px-8 py-4 bg-[#d4a853] text-[#0a1628] font-bold uppercase tracking-wider overflow-hidden" data-id="element-352">
              <span className="relative z-10 flex items-center gap-2" data-id="element-353">
                Get a Sourcing Quote
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-354" />
              </span>
              <div className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-out" data-id="element-355" />
            </motion.button>

            <motion.button whileHover={{
            scale: 1.02,
            backgroundColor: 'rgba(255,255,255,0.05)'
          }} whileTap={{
            scale: 0.98
          }} onClick={() => scrollToSection('services')} className="px-8 py-4 border border-white/20 text-white font-bold uppercase tracking-wider hover:border-[#d4a853] transition-colors" data-id="element-356">
              Our Services
            </motion.button>
          </div>

          {/* Quick Service Pills */}
          <div className="flex flex-wrap gap-3 mb-12" data-id="element-357">
            {[{
            label: 'Hypermarket Sourcing',
            icon: ShoppingCart
          }, {
            label: 'Manufacturer Representation',
            icon: Users
          }, {
            label: 'Market Entry',
            icon: Globe
          }, {
            label: 'Procurement',
            icon: PackageCheck
          }].map((pill, i) => <motion.div key={pill.label} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.8 + i * 0.1
          }} className="flex items-center gap-2 px-4 py-2 bg-[#1a2d4a]/50 border border-white/10 rounded-full text-xs font-bold uppercase tracking-wider text-gray-300 hover:border-[#d4a853] hover:text-[#d4a853] transition-colors cursor-default" data-id="element-358">
                <pill.icon size={14} className="text-[#d4a853]" data-id="element-359" />
                {pill.label}
              </motion.div>)}
          </div>

          {/* Stats / Trust Indicators */}
          <div className="grid grid-cols-3 gap-8 border-t border-white/10 pt-8" data-id="element-360">
            {[{
            label: '3 Regions',
            value: 'UK • EU • Asia',
            icon: Globe
          }, {
            label: 'Retail Partners',
            value: '50+',
            icon: Store
          }, {
            label: 'Manufacturers',
            value: '500+',
            icon: Building2
          }].map((stat, i) => <motion.div key={stat.label} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5 + i * 0.1,
            duration: 0.5
          }} data-id="element-361">
                <stat.icon className="w-5 h-5 text-[#d4a853] mb-2" data-id="element-362" />
                <div className="text-2xl font-bold text-white" data-id="element-363">
                  {stat.value}
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-364">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>

      {/* Right Geometric Section */}
      <div className="relative w-full lg:w-[40%] h-[50vh] lg:h-auto bg-[#1a2d4a] overflow-hidden" data-id="element-365">
        <GeometricPattern data-id="element-366" />

        {/* Gradient Overlay for Mobile blending */}
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#0a1628] to-transparent lg:hidden" data-id="element-367" />

        {/* Floating Info Card */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 1,
        duration: 0.8
      }} className="absolute bottom-8 right-8 bg-[#0a1628]/80 backdrop-blur-md border border-white/10 p-6 max-w-xs hidden lg:block" data-id="element-368">
          <div className="flex items-start gap-4" data-id="element-369">
            <div className="w-2 h-2 mt-2 rounded-full bg-green-500 animate-pulse" data-id="element-370" />
            <div data-id="element-371">
              <h3 className="text-white font-bold text-sm mb-1" data-id="element-372">
                Active Across 3 Continents
              </h3>
              <p className="text-xs text-gray-400" data-id="element-373">
                Currently managing sourcing and market entry projects with major
                retail chains across the UK, Europe, and Asia.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}